import React, { useState } from 'react';
import { X } from 'lucide-react';

const JOURS = ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi', 'dimanche'];

export const AgentModal = ({ agent, onClose, onSave, apiUrl }) => {
  const isEditing = !!agent;

  const [name, setName]   = useState(isEditing ? agent.data.name : '');
  const [phone, setPhone] = useState(isEditing ? agent.data.phone : '');
  const [saving, setSaving] = useState(false);

  const initDisponibilite = () => {
    const dispo = {};
    JOURS.forEach(jour => {
      const existing = isEditing && agent.data.disponibilite && agent.data.disponibilite[jour];
      dispo[jour] = { enabled: !!existing, start: existing ? existing.start : '9', end: existing ? existing.end : '19' };
    });
    return dispo;
  };

  const [disponibilite, setDisponibilite] = useState(initDisponibilite);

  const toggleJour = (jour) => {
    setDisponibilite(prev => ({ ...prev, [jour]: { ...prev[jour], enabled: !prev[jour].enabled } }));
  };

  const updateHeure = (jour, field, value) => {
    setDisponibilite(prev => ({ ...prev, [jour]: { ...prev[jour], [field]: value } }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !phone.trim()) { alert('Nom et téléphone requis'); return; }
    setSaving(true);
    const token = localStorage.getItem('admin_token');
    const dispoFinal = {};
    JOURS.forEach(jour => {
      if (disponibilite[jour].enabled) {
        dispoFinal[jour] = { start: disponibilite[jour].start, end: disponibilite[jour].end };
      }
    });
    const payload = { name: name.trim(), phone: phone.trim(), disponibilite: dispoFinal };
    try {
      const url    = isEditing ? `${apiUrl}/api/chat/agents/${agent.id}` : `${apiUrl}/api/chat/agents/add`;
      const method = isEditing ? 'PUT' : 'POST';
      const resp = await fetch(url, {
        method,
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (resp.ok) { onSave(); }
      else { const err = await resp.json(); alert(`Erreur: ${err.detail || 'Inconnue'}`); }
    } catch { alert('Erreur réseau'); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-xl max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-xl font-bold">
              {isEditing ? `✏️ Modifier ${agent.data.name}` : '➕ Ajouter un Agent'}
            </h3>
            <button onClick={onClose} className="hover:bg-gray-100 p-2 rounded-full"><X size={24} /></button>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-semibold mb-1">Nom de l'agent *</label>
              <input type="text" value={name} onChange={e => setName(e.target.value)}
                className="w-full border rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
                placeholder="Ex: Sophie" required />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-1">Numéro de téléphone (SMS) *</label>
              <input type="text" value={phone} onChange={e => setPhone(e.target.value)}
                className="w-full border rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400 font-mono"
                placeholder="+33612345678" required />
              <p className="text-xs text-gray-500 mt-1">Format international : +33XXXXXXXXX</p>
            </div>
            <div>
              <label className="block text-sm font-semibold mb-2">
                Horaires de disponibilité
                <span className="ml-2 text-xs text-gray-500 font-normal">(laissez vide = joignable 24h/24)</span>
              </label>
              <div className="border rounded-lg overflow-hidden">
                {JOURS.map((jour, idx) => (
                  <div key={jour} className={`flex items-center p-3 ${idx % 2 === 0 ? 'bg-gray-50' : 'bg-white'}`}>
                    <button type="button" onClick={() => toggleJour(jour)}
                      className={`w-10 h-5 rounded-full transition-colors flex-shrink-0 mr-3 ${disponibilite[jour].enabled ? 'bg-blue-500' : 'bg-gray-300'}`}>
                      <div className={`w-4 h-4 bg-white rounded-full shadow transition-transform mx-0.5 ${disponibilite[jour].enabled ? 'translate-x-5' : 'translate-x-0'}`} />
                    </button>
                    <span className={`w-24 capitalize text-sm font-medium ${disponibilite[jour].enabled ? 'text-gray-800' : 'text-gray-400'}`}>
                      {jour}
                    </span>
                    {disponibilite[jour].enabled ? (
                      <div className="flex items-center space-x-2 ml-auto">
                        <select value={disponibilite[jour].start} onChange={e => updateHeure(jour, 'start', e.target.value)}
                          className="border rounded px-2 py-1 text-sm">
                          {Array.from({length: 24}, (_, i) => (
                            <option key={i} value={String(i)}>{i}h00</option>
                          ))}
                        </select>
                        <span className="text-gray-500 text-sm">—</span>
                        <select value={disponibilite[jour].end} onChange={e => updateHeure(jour, 'end', e.target.value)}
                          className="border rounded px-2 py-1 text-sm">
                          {Array.from({length: 24}, (_, i) => (
                            <option key={i} value={String(i)}>{i}h00</option>
                          ))}
                        </select>
                      </div>
                    ) : (
                      <span className="ml-auto text-xs text-gray-400 italic">Indisponible</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
            <div className="flex space-x-3 pt-2">
              <button type="submit" disabled={saving}
                className="flex-1 bg-blue-500 hover:bg-blue-600 disabled:bg-gray-300 text-white px-4 py-3 rounded-lg font-semibold flex items-center justify-center space-x-2">
                {saving ? <span>Sauvegarde...</span> : <span>{isEditing ? '✅ Enregistrer les modifications' : "✅ Ajouter l'agent"}</span>}
              </button>
              <button type="button" onClick={onClose}
                className="px-6 py-3 bg-gray-200 hover:bg-gray-300 rounded-lg text-gray-700 font-semibold">
                Annuler
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
